// Draft Privacy Policy — matches TOS section/format. Replace with final legal-reviewed copy before launch.
const PRIVACY = {
  header: 'Privacy Policy',
  updated: 'Last Updated: July 29, 2026',
  preamble: [
    'This Privacy Policy explains how Arvance, LLC ("Company," "we," "us," or "our") collects, uses, discloses, retains, and protects personal information in connection with your access to and use of the Company\'s software-as-a-service platform, related applications, data, reports, analytics, and associated services (collectively, the "Software"). This Policy is incorporated into the Terms of Service by reference.',
  ],
  sections: [
    {
      num: '1',
      title: 'SCOPE AND ACCEPTANCE',
      blocks: [
        { t: 'p', text: 'This Policy applies to personal information we process when you visit our website, create an account, subscribe to a plan, communicate with us, or otherwise interact with the Software. By accessing or using the Software, you acknowledge that you have read and understood this Policy.' },
        { t: 'p', text: 'This Policy does not apply to third-party websites, products, or services that we do not own or control, even if they link to or from the Software.' },
      ],
    },
    {
      num: '2',
      title: 'INFORMATION WE COLLECT',
      blocks: [
        { t: 'p', text: 'We collect the following categories of information:' },
        {
          t: 'ul',
          items: [
            'Account Information: name, email address, hashed password, company name and size, role, and how you heard about us.',
            'Subscription and Billing Information: plan tier, billing cycle, subscription status, trial dates, and Stripe customer identifiers. Payment card details are collected and stored by Stripe on their PCI-compliant infrastructure; we do not receive, transmit, or store full card numbers.',
            'User Data: properties, deals, comparables, projections, tags, notes, custom templates, and other content you submit to the Software.',
            'Communications: messages you send to our support team and metadata about those communications.',
            'Usage and Device Data: log data such as IP address, user agent, referring pages, timestamps, feature interactions, and diagnostic information collected automatically as you use the Software.',
            'Cookies and Similar Technologies: session cookies and authentication tokens strictly necessary to operate the Software. See Section 9.',
          ],
        },
        { t: 'p', text: 'We do not knowingly collect biometric identifiers, precise geolocation data, or special categories of sensitive personal information.' },
      ],
    },
    {
      num: '3',
      title: 'HOW WE USE INFORMATION',
      blocks: [
        { t: 'p', text: 'We use personal information for the following purposes:' },
        {
          t: 'ul',
          items: [
            'To provide, maintain, secure, and improve the Software and the features you request;',
            'To create and manage your account, authenticate access, and enforce plan limits;',
            'To process subscriptions, invoices, receipts, refunds, and other billing operations through Stripe;',
            'To send transactional communications, including password reset, receipts, subscription confirmations, trial expiry reminders, and material Policy or Terms updates;',
            'To respond to support requests, feedback, and inquiries;',
            'To detect, prevent, investigate, and address fraud, abuse, security incidents, or violations of the Terms of Service;',
            'To comply with legal obligations, respond to lawful requests, and enforce our legal rights.',
          ],
        },
        { t: 'p', text: 'We do not use your User Data to train third-party generative AI models, and we do not sell your personal information.' },
      ],
    },
    {
      num: '4',
      title: 'LEGAL BASES FOR PROCESSING (EEA / UK USERS)',
      blocks: [
        { t: 'p', text: 'If you access the Software from the European Economic Area, the United Kingdom, or Switzerland, we process your personal information on the following legal bases:' },
        {
          t: 'ul',
          items: [
            'Performance of a contract — to provide the Software you have subscribed to;',
            'Legitimate interests — to secure the Software, prevent abuse, and improve the product, balanced against your rights and freedoms;',
            'Compliance with legal obligations — to respond to lawful requests and satisfy tax and accounting requirements;',
            'Consent — where required by applicable law and only for the specific purpose disclosed to you at the time of collection.',
          ],
        },
      ],
    },
    {
      num: '5',
      title: 'HOW WE SHARE INFORMATION',
      blocks: [
        { t: 'p', text: 'We do not sell personal information. We share limited information only with the following categories of recipients:' },
        {
          t: 'ul',
          items: [
            'Service Providers: vendors that process personal information on our behalf under written data-protection terms, including Stripe (payments), Resend (transactional email delivery), MongoDB Atlas (database hosting), and Render (application hosting).',
            'Professional Advisors: legal, accounting, and audit professionals bound by confidentiality obligations.',
            'Corporate Transactions: in connection with a merger, acquisition, financing, or sale of assets, subject to standard confidentiality protections and continued application of this Policy.',
            'Legal and Safety: where we believe in good faith that disclosure is required by law, subpoena, court order, or to protect the rights, property, or safety of the Company, our users, or the public.',
          ],
        },
      ],
    },
    {
      num: '6',
      title: 'INTERNATIONAL DATA TRANSFERS',
      blocks: [
        { t: 'p', text: 'Personal information may be processed and stored in the United States and other jurisdictions where we or our service providers operate. When we transfer personal information out of the EEA, UK, or Switzerland, we rely on Standard Contractual Clauses, adequacy decisions, or other lawful transfer mechanisms as required by applicable law.' },
      ],
    },
    {
      num: '7',
      title: 'DATA RETENTION',
      blocks: [
        { t: 'p', text: 'We retain personal information for as long as your account is active or as needed to provide the Software. When you request account deletion, we will remove your workspace and associated User Data within thirty (30) days, subject to legal retention requirements such as invoicing, tax, and audit records, which may be retained for the period required by applicable law.' },
        { t: 'p', text: 'Backups may persist for a limited additional period consistent with our standard backup rotation before being securely overwritten.' },
      ],
    },
    {
      num: '8',
      title: 'SECURITY',
      blocks: [
        { t: 'p', text: 'We implement industry-standard administrative, technical, and organizational safeguards designed to protect personal information, including encrypted transport (HTTPS/TLS), hashed passwords (bcrypt), scoped access tokens, least-privilege database access, and audit logging of privileged actions.' },
        { t: 'p', text: 'No system can be guaranteed to be one hundred percent secure. If you believe your account or data has been compromised, contact us immediately at support@reilm.io.' },
      ],
    },
    {
      num: '9',
      title: 'COOKIES AND SIMILAR TECHNOLOGIES',
      blocks: [
        { t: 'p', text: 'We use a limited set of strictly necessary cookies and browser storage to authenticate sessions, remember your preferences, and protect against fraud and abuse. We do not use third-party advertising cookies, cross-site tracking, or behavioral advertising networks.' },
        { t: 'p', text: 'You may control cookies through your browser settings. Disabling strictly necessary cookies may prevent you from signing in or using core features.' },
      ],
    },
    {
      num: '10',
      title: 'YOUR PRIVACY RIGHTS',
      blocks: [
        { t: 'p', text: 'Depending on your jurisdiction, you may have the following rights with respect to your personal information:' },
        {
          t: 'ul',
          items: [
            'Access — to obtain a copy of the personal information we hold about you;',
            'Correction — to request correction of inaccurate or incomplete information;',
            'Deletion — to request deletion of your personal information, subject to legal retention requirements;',
            'Portability — to request a copy of your data in a structured, commonly used, machine-readable format;',
            'Restriction or Objection — to restrict or object to certain processing based on legitimate interests;',
            'Withdrawal of Consent — where processing is based on consent, to withdraw that consent at any time.',
          ],
        },
        { t: 'p', text: 'To exercise any of these rights, email support@reilm.io from the address associated with your account. We will respond within the period required by applicable law. You also have the right to lodge a complaint with a supervisory authority in your jurisdiction.' },
      ],
    },
    {
      num: '11',
      title: 'CALIFORNIA PRIVACY RIGHTS (CCPA / CPRA)',
      blocks: [
        { t: 'p', text: 'California residents have the right to know what personal information we collect, use, disclose, and retain; to request deletion; to correct inaccurate information; to opt out of any sale or sharing for cross-context behavioral advertising; and to limit the use of sensitive personal information. We do not sell personal information and do not share personal information for cross-context behavioral advertising.' },
        { t: 'p', text: 'To exercise your California rights, email support@reilm.io. We will not discriminate against you for exercising any right under the CCPA/CPRA.' },
      ],
    },
    {
      num: '12',
      title: 'CHILDREN\'S PRIVACY',
      blocks: [
        { t: 'p', text: 'The Software is intended for business users aged eighteen (18) or older and is not directed to children. We do not knowingly collect personal information from anyone under the age of thirteen (13), or under sixteen (16) in the EEA and UK. If we learn that we have collected personal information from a child in violation of this Policy, we will delete it promptly.' },
      ],
    },
    {
      num: '13',
      title: 'AUTOMATED DECISION-MAKING',
      blocks: [
        { t: 'p', text: 'The Software generates estimates, projections, and analyses based on data and assumptions you provide. Outputs are informational only and do not constitute solely-automated decisions that produce legal or similarly significant effects on you. You are responsible for reviewing, verifying, and independently deciding whether to act on any Output. See the Terms of Service, Section 9 (Outputs and Permitted Use).' },
      ],
    },
    {
      num: '14',
      title: 'THIRD-PARTY SERVICES',
      blocks: [
        { t: 'p', text: 'The Software integrates with third-party services, including Stripe for payments and Resend for email delivery. Your use of those services is governed by the applicable third-party terms and privacy notices, which we encourage you to review. We are not responsible for the privacy practices of third parties.' },
      ],
    },
    {
      num: '15',
      title: 'CHANGES TO THIS POLICY',
      blocks: [
        { t: 'p', text: 'We may update this Policy from time to time to reflect changes to the Software, applicable law, or our practices. When we make material changes, we will notify you by email to the address on file or by a prominent notice within the Software before the change takes effect. Your continued use of the Software after the effective date constitutes acceptance of the updated Policy.' },
      ],
    },
    {
      num: '16',
      title: 'CONTACT US',
      blocks: [
        { t: 'p', text: 'Questions, requests, or complaints regarding this Policy or our privacy practices should be directed to:' },
        { t: 'p', text: 'Arvance, LLC — support@reilm.io' },
      ],
    },
  ],
}

export default PRIVACY
